/**
 * Resumen del negocio: MRR, registros, activación, uso y costo del período con su
 * variación, series por día, embudo, cohortes semanales y embudo por campaña.
 */
import { useState } from 'react'
import { useApp } from '../../components/AppContext'
import { Embudo, GraficoSerie, MapaCohortes } from '../../components/charts'
import { ErrorBox, Skeleton } from '../../components/ui'
import { clp, compacto, pct, usd, usdEje } from '../../lib/adminFormat'
import { useAsync, useDocumentTitle } from '../../lib/hooks'
import { Kpi, Periodo, Seccion } from './common'

const horas = (min: number) => `${compacto(Math.round((min / 60) * 10) / 10)} h`

export function Resumen() {
  useDocumentTitle('Administración')
  const { api } = useApp()
  const [dias, setDias] = useState(30)
  const r = useAsync(() => api.adminResumen(dias), [api, dias])
  const campanas = useAsync(() => api.adminEmbudo(dias), [api, dias])
  const d = r.data

  return (
    <div className="stack" style={{ gap: 18 }}>
      <div className="admin-toolbar">
        <span className="faint grow" style={{ fontSize: 12 }}>
          Las variaciones comparan con los {dias} días anteriores. Días en hora de Chile.
        </span>
        <Periodo dias={dias} onChange={setDias} />
      </div>
      {r.error && <ErrorBox error={r.error} onRetry={() => void r.reload()} />}
      {!d && !r.error && <Skeleton h={160} />}
      {d && (
        <>
          <section className="card" aria-labelledby="mrr-label">
            <div className="row wrap" style={{ gap: 24, alignItems: 'flex-end' }}>
              <div className="grow">
                <div id="mrr-label" className="kpi-label">
                  Ingresos mensuales recurrentes (MRR)
                </div>
                <div className="hero-num">{clp(d.mrrClp)}</div>
                <div className="faint" style={{ fontSize: 12 }}>
                  {d.suscripcionesPagadas} suscripcion{d.suscripcionesPagadas === 1 ? '' : 'es'}{' '}
                  pagada{d.suscripcionesPagadas === 1 ? '' : 's'} · precio de lista con IVA, sin
                  descuentos · {d.cortesias} cortesía{d.cortesias === 1 ? '' : 's'} vigente
                  {d.cortesias === 1 ? '' : 's'} (no suman)
                </div>
              </div>
              <dl className="kv" style={{ margin: 0 }}>
                <dt>Personas</dt>
                <dd>{d.usuariosTotales.toLocaleString('es-CL')}</dd>
                <dt>Activas 7 días</dt>
                <dd>{d.activos7.toLocaleString('es-CL')}</dd>
                <dt>Activas 30 días</dt>
                <dd>{d.activos30.toLocaleString('es-CL')}</dd>
              </dl>
            </div>
          </section>

          <div className="kpis">
            <Kpi
              label="Registros"
              valor={d.registros.valor.toLocaleString('es-CL')}
              comparado={d.registros}
              tendencia={d.serie.map((p) => p.registros)}
            />
            <Kpi
              label="Activados"
              valor={d.activados.valor.toLocaleString('es-CL')}
              comparado={d.activados}
              nota={pct(d.activados.valor, d.registros.valor)}
            />
            <Kpi
              label="Reuniones"
              valor={d.reuniones.valor.toLocaleString('es-CL')}
              comparado={d.reuniones}
              tendencia={d.serie.map((p) => p.reuniones)}
            />
            <Kpi
              label="Horas procesadas"
              valor={horas(d.minutos.valor)}
              comparado={d.minutos}
              tendencia={d.serie.map((p) => p.minutos)}
            />
            <Kpi
              label="Costo de proveedores"
              valor={usd(d.costoUsd.valor)}
              comparado={d.costoUsd}
              subirEsBueno={false}
              tendencia={d.serie.map((p) => p.costoUsd)}
            />
            <Kpi
              label="Costo por hora"
              valor={d.minutos.valor ? usd(d.costoUsd.valor / (d.minutos.valor / 60)) : '—'}
              nota={`≈ ${clp(
                d.minutos.valor
                  ? (d.costoUsd.valor / (d.minutos.valor / 60)) * d.tipoCambioClpUsd
                  : 0
              )}`}
            />
          </div>

          <div className="admin-grid">
            <div className="span-6">
              <GraficoSerie
                titulo="Reuniones por día"
                subtitulo="Reuniones completadas (con notas)"
                puntos={d.serie.map((p) => ({ etiqueta: p.dia, valor: p.reuniones }))}
                formato={(n) => compacto(n)}
                nombreValor="Reuniones"
                tipo="barras"
              />
            </div>
            <div className="span-6">
              <GraficoSerie
                titulo="Registros por día"
                subtitulo="Cuentas nuevas"
                puntos={d.serie.map((p) => ({ etiqueta: p.dia, valor: p.registros }))}
                formato={(n) => compacto(n)}
                nombreValor="Registros"
                tipo="barras"
              />
            </div>
            <div className="span-6">
              <GraficoSerie
                titulo="Horas procesadas por día"
                puntos={d.serie.map((p) => ({ etiqueta: p.dia, valor: p.minutos / 60 }))}
                formato={(n) => `${compacto(Math.round(n * 10) / 10)} h`}
                nombreValor="Horas"
              />
            </div>
            <div className="span-6">
              <GraficoSerie
                titulo="Costo de proveedores por día"
                subtitulo="Transcripción y notas"
                puntos={d.serie.map((p) => ({ etiqueta: p.dia, valor: p.costoUsd }))}
                formato={usd}
                formatoEje={usdEje}
                nombreValor="Costo"
              />
            </div>
            <div className="span-4">
              <Seccion titulo="Embudo del período">
                <Embudo
                  pasos={[
                    { nombre: 'Se registraron', valor: d.embudo.registros },
                    { nombre: 'Tuvieron una reunión', valor: d.embudo.activados },
                    {
                      nombre: 'Volvieron otra semana',
                      valor: d.embudo.recurrentes,
                      ayuda: 'hábito'
                    },
                    { nombre: 'Pagan', valor: d.embudo.pagando }
                  ]}
                />
              </Seccion>
            </div>
            <div className="span-8">
              <MapaCohortes cohortes={d.cohortes} />
            </div>
          </div>
        </>
      )}

      <Seccion titulo="Embudo por campaña">
        {campanas.error && (
          <ErrorBox error={campanas.error} onRetry={() => void campanas.reload()} />
        )}
        {campanas.data && campanas.data.filas.length === 0 && (
          <p className="faint" style={{ margin: 0 }}>
            Todavía no hay registros en este período.
          </p>
        )}
        {campanas.data && campanas.data.filas.length > 0 && (
          <div style={{ overflowX: 'auto' }}>
            <table className="simple">
              <caption className="sr">Registros por campaña</caption>
              <thead>
                <tr>
                  <th scope="col">Fuente</th>
                  <th scope="col">Medio</th>
                  <th scope="col">Campaña</th>
                  <th scope="col" className="num">
                    Registros
                  </th>
                  <th scope="col" className="num">
                    Activados
                  </th>
                  <th scope="col" className="num">
                    Pagando
                  </th>
                </tr>
              </thead>
              <tbody>
                {campanas.data.filas.map((f) => (
                  <tr key={`${f.fuente}|${f.medio}|${f.campana}`}>
                    <td>{f.fuente}</td>
                    <td>{f.medio || '—'}</td>
                    <td>{f.campana || '—'}</td>
                    <td className="num">{f.registros}</td>
                    <td className="num">
                      {f.activados} <span className="faint">· {pct(f.activados, f.registros)}</span>
                    </td>
                    <td className="num">
                      {f.pagando} <span className="faint">· {pct(f.pagando, f.registros)}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <p className="faint" style={{ margin: 0, fontSize: 12 }}>
          La fuente sale de los utm_* del anuncio o de la publicación (sin cookies).
        </p>
      </Seccion>
    </div>
  )
}
